"use client";
import React from "react";
import type { LineData } from "lightweight-charts";
import { ContentBox } from "./RecentCoin.styled";
import { Colors } from "themes/color-palette";
import { Body } from "app/_components/components/Text/Body";
import { Heading } from "app/_components/components/Text/Heading";
import { useTranslations } from "i18n";
import { FILES } from "i18n/constants";
import { formatPercentage, formatSupply } from "utils/currencies/formatters";
import { formatPrice, formatTimestampToUTC } from "utils/coinPrices";
import { useCurrentTimeInSeconds } from "hooks/useCurrentTime";
import { useSingleAssetPriceFetcher } from "components/pages/prices/useSortedPriceFetcher";
import { DeltaUtils } from "@hubble/web";
import type { CoinPriceOverview } from "types/prices";

interface RecentCoinProps {
  initialOverview: CoinPriceOverview;
}

const RecentCoin = (props: RecentCoinProps) => {
  const { initialOverview } = props;
  const { priceData } = useSingleAssetPriceFetcher({
    coinOverview: props.initialOverview
  });
  const priceOverview = initialOverview?.priceOverview;
  const currentTime = useCurrentTimeInSeconds();

  const coinName = priceData?.name ?? priceData?.toDisplayName;
  const coinSymbol = priceData?.coin ?? priceData?.symbol;
  const currentPriceUnformatted =
    priceOverview?.price ??
    (priceData?.priceRange?.length
      ? priceData.priceRange[priceData.priceRange.length - 1].value
      : 0);

  // Use the last chart data point if available, otherwise use current time
  const currentPriceTime = priceData?.priceRange?.length
    ? priceData.priceRange[priceData.priceRange.length - 1].time
    : currentTime;

  // Use DeltaUtils for consistent percentage calculation with <Chart />
  const chartData =
    priceData?.priceRange?.map((point: LineData) => ({
      time: point.time,
      value: point.value
    })) || [];

  const { label: deltaLabel } =
    chartData.length > 0
      ? DeltaUtils.getDeltaFromChartData(chartData, "line", formatPrice)
      : { label: "" };

  // Extract percentage from deltaLabel (e.g., "−$132.38 (−2.8%)" -> "−2.8%")
  const percentageMatch = deltaLabel.match(/\((.*?%)\)/);
  const consistentPercentage = percentageMatch ? percentageMatch[1] : "";

  // Use consistent calculation or fallback to API data
  const percentChange24HFormatted =
    consistentPercentage ||
    formatPercentage(
      priceOverview?.["24hrPercentDelta"] ??
        priceData?.["24hrPercentDelta"] ??
        ""
    ) ||
    "";

  // Determine if price decreased based on consistent percentage
  const priceDecreased = consistentPercentage
    ? consistentPercentage.includes("−") || consistentPercentage.includes("-")
    : Number(
        priceOverview?.["24hrPercentDelta"] ??
          priceData?.["24hrPercentDelta"] ??
          0
      ) < 0;

  const supplyAmountUnformatted =
    priceOverview?.supply?.value ?? priceData?.marketCap?.value;

  const t = useTranslations(FILES.PRICES);

  return (
    <ContentBox>
      {priceData && (
        <React.Fragment>
          <Heading size="md" className="mb-8">
            {t("pricesPageGenericTitle", {
              name: coinName,
              coin: coinSymbol
            })}
          </Heading>
          <Body size="sm" className="mb-8">
            {percentChange24HFormatted && (
              <React.Fragment>
                {t("coinPriceDeltaDescription", {
                  coinName,
                  percentChange24H: percentChange24HFormatted,
                  deltaString: t(priceDecreased ? "decreased" : "increased")
                })}{" "}
              </React.Fragment>
            )}
            {t("coinPriceDescription", {
              coinName,
              currentPrice: formatPrice(currentPriceUnformatted)
            })}
            {supplyAmountUnformatted && (
              <React.Fragment>
                {" "}
                {t("coinPriceSupplyDescription", {
                  supplyAmount: formatSupply(supplyAmountUnformatted),
                  coin: coinSymbol
                })}
              </React.Fragment>
            )}
          </Body>
          <Body size="sm" style={{ color: Colors.gray[600] }}>
            {t("lastUpdatedText", {
              timeString: formatTimestampToUTC(currentPriceTime)
            })}
          </Body>
        </React.Fragment>
      )}
    </ContentBox>
  );
};
export default RecentCoin;
